import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import { initLenis } from "@/lib/scroll";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/Marquee";
import Products from "@/components/Products";
import Manifesto from "@/components/Manifesto";
import DisclaimerBlock from "@/components/DisclaimerBlock";
import OrderSection from "@/components/OrderSection";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import PaymentSuccess from "@/components/PaymentSuccess";
import PaymentCancel from "@/components/PaymentCancel";
import PolicyPage from "@/components/PolicyPage";
import LabResults from "@/components/LabResults";
import BacteriostaticWater from "@/components/BacteriostaticWater";
import Wholesale from "@/components/Wholesale";
import Shipping from "@/components/Shipping";
import Faq from "@/components/Faq";
import Seo from "@/components/Seo";
import NotFound from "@/components/NotFound";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

const AdminPage = lazy(() => import("@/components/AdminPage"));

function HomePage() {
  useEffect(() => {
    initLenis();
  }, []);

  return (
    <>
      <Seo
        title="Bacteriostatic Water for Labs | Lab2o"
        path="/"
        description="Research-grade bacteriostatic water (0.9% benzyl alcohol). Lot-tested, CoA posted, fast US shipping."
      />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Lab2o",
          url: "https://lab2o.com/",
          logo: "https://lab2o.com/images/hero-vials.webp",
          email: "orders@lab2o.com",
        })}
      </script>
      <Navbar />
      <main>
        <Hero />
        <EditorialMarquee />
        <Products />
        <Manifesto />
        <DisclaimerBlock />
        <OrderSection />
      </main>
      <Footer />
      <CartDrawer />
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/bacteriostatic-water" element={<><BacteriostaticWater /><CartDrawer /></>} />
      <Route path="/wholesale" element={<Wholesale />} />
      <Route path="/shipping" element={<Shipping />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/lab-results" element={<LabResults />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin" element={<Suspense fallback={null}><AdminPage /></Suspense>} />
      <Route path="/policies/refund" element={<Navigate to="/policies/refunds" replace />} />
      <Route path="/policies/:slug" element={<PolicyPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function AppShell({ children }) {
  return (
    <HelmetProvider>
      <CartProvider>
        <div className="App font-inter">
          {children}
          <Toaster position="bottom-right" />
        </div>
      </CartProvider>
    </HelmetProvider>
  );
}

function App() {
  return (
    <AppShell>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppShell>
  );
}

export default App;
